import { Dispatch, useEffect, useState } from 'react';
import { FormikErrors, FormikTouched } from 'formik';
import {
  DropdownLink,
  DropdownList,
  DropdownTrigger,
  FormHelpText,
  Tooltip,
  TooltipTrigger,
  Type,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';
import { fromMomentObjectToISO } from '@helpers/dateTimeUtils';
import { DateRangePickerWrapper } from '../DateRangePickerWrapper';
import { Action, ACTION_TYPES } from '../reducer';
import {
  DateRangeLabelContainer,
  StyledDropdown,
  StyledFormGroup,
  StyledIconHelpCircle,
} from '../styles';
import { DateRangeMoment, DATE_RANGE_TYPE, FormValues } from '../types';

export interface DropdownItem {
  text: string;
  id: DATE_RANGE_TYPE;
}

export interface DateRangeDropdownProps {
  selectedDateRangeTypeId: string;
  lastSavedCustomDateRange: DateRangeMoment;
  errors: FormikErrors<FormValues>;
  touched: FormikTouched<FormValues>;
  dispatch: Dispatch<Action>;
  onboardingPopover: React.ReactNode | undefined;
}

export const DateRangeDropdown = ({
  selectedDateRangeTypeId,
  lastSavedCustomDateRange,
  errors,
  touched,
  dispatch,
  onboardingPopover = undefined,
}: DateRangeDropdownProps) => {
  const t = useT();

  const dropdownListId = 'date-range-dropdown-list';
  const triggerId = 'date-range-trigger';
  const items = [
    {
      text: t(
        'atlas.date_range_types.last_7_days',
        'Last 7 days',
        'Refers to previous 7 days',
      ),
      id: DATE_RANGE_TYPE.LAST_7_DAYS,
    },
    {
      text: t(
        'atlas.date_range_types.last_28_days',
        'Last 28 days',
        'Refers to previous 28 days',
      ),
      id: DATE_RANGE_TYPE.LAST_28_DAYS,
    },
    {
      text: t(
        'atlas.date_range_types.last_90_days',
        'Last 90 days',
        'Refers to previous 90 days',
      ),
      id: DATE_RANGE_TYPE.LAST_90_DAYS,
    },
    {
      text: t(
        'atlas.date_range_types.custom_date_range',
        'Custom date range',
        'Refers to a custom date range',
      ),
      id: DATE_RANGE_TYPE.CUSTOM_DATES,
    },
    {
      text: t(
        'atlas.date_range_types.all_time',
        'All time',
        'Refers to all time',
      ),
      id: DATE_RANGE_TYPE.ALL_TIME,
    },
  ];

  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [showDateRangePicker, setShowDateRangePicker] =
    useState<boolean>(false);
  const [selected, setSelected] = useState<number>(0);

  useEffect(() => {
    if (showDropdown) {
      const element = document.getElementById(dropdownListId) as HTMLElement;
      element.focus();
    }
  }, [showDropdown, dropdownListId]);

  const isAllTimeDateRangeType =
    selectedDateRangeTypeId === DATE_RANGE_TYPE.ALL_TIME;
  const isCustomDateRangeType =
    selectedDateRangeTypeId === DATE_RANGE_TYPE.CUSTOM_DATES;
  const allTimeDateRangeInFilename = t(
    'atlas.date_range_types.all_time',
    'All time',
    'Refers to all time',
  );

  const datesTouched = touched.startDate || touched.endDate;
  const hasStartDateError =
    typeof errors.startDate === 'string' &&
    errors.startDate.length > 0 &&
    datesTouched;
  const hasEndDateError =
    typeof errors.endDate === 'string' &&
    errors.endDate.length > 0 &&
    datesTouched;

  const onChange = (id: DATE_RANGE_TYPE) => {
    sendEvent({
      eventCategory: EVENT_CATEGORY.CATALOG_EXPORTS_FORM,
      eventAction: EVENT_ACTION.SELECT_DATE_RANGE_TYPE,
      eventLabel: `Selected date range type: ${id}`,
      product: GA_PRODUCT_CATALOG,
    });

    dispatch({
      type: ACTION_TYPES.SELECT_EXPORT_TYPE,
      payload: {
        dateRangeType: id,
        // Only the All Time placeholder text has to be passed through to the reducer because it needs to be translated in this component
        filename: isAllTimeDateRangeType ? allTimeDateRangeInFilename : '',
      },
    });
  };

  const handleClick = (id: DATE_RANGE_TYPE) => {
    if (id === DATE_RANGE_TYPE.CUSTOM_DATES) {
      setShowDateRangePicker(true);
    }
    setShowDropdown(prevShow => !prevShow);
    onChange(id);
  };

  const customDateRangeFormatted = `${fromMomentObjectToISO(
    lastSavedCustomDateRange.startDate,
  )} — ${fromMomentObjectToISO(lastSavedCustomDateRange.endDate)}`;

  const lastSavedCustomDateRangeInFilename =
    lastSavedCustomDateRange.startDate && lastSavedCustomDateRange.endDate
      ? customDateRangeFormatted
      : t(
          'atlas.date_range_types.custom_date_range',
          'Custom date range',
          'Refers to a custom date range',
        );

  const renderDropdownText = () => {
    if (isCustomDateRangeType) {
      if (hasStartDateError || hasEndDateError) {
        return t(
          'atlas.date_range_types.custom_date_range',
          'Custom date range',
          'Refers to a custom date range',
        );
      }

      return lastSavedCustomDateRangeInFilename;
    }

    return items.find(item => item.id === selectedDateRangeTypeId)?.text;
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const upArrow = 'ArrowUp';
    const downArrow = 'ArrowDown';
    const enter = 'Enter';
    const escape = 'Escape';
    const tab = 'Tab';
    const space = ' ';

    switch (e.key) {
      case upArrow: {
        e.preventDefault();
        if (selected !== 0) {
          onChange(items[selected - 1].id);
          setSelected(selected - 1);
        }
        break;
      }

      case downArrow: {
        e.preventDefault();
        if (selected < items.length - 1) {
          onChange(items[selected + 1].id);
          setSelected(selected + 1);
        }
        break;
      }

      case enter:
      case escape:
      case space:
      case tab: {
        e.preventDefault();
        setShowDropdown(false);
        if (isCustomDateRangeType) {
          setShowDateRangePicker(true);
        }

        const element = document.getElementById(triggerId) as HTMLElement;
        element.focus();
        break;
      }

      default: {
        break;
      }
    }
  };

  return (
    <StyledFormGroup
      label={
        <DateRangeLabelContainer>
          <Type variant="violaBold" semanticColor="textBase">
            {t(
              'atlas.labels.date_range_required',
              'Date range',
              'Input label for Date range (i.e. 2022-01-01 to 2023-01-01)',
            )}
          </Type>
          <TooltipTrigger
            overlay={
              showTooltip && (
                <Tooltip>
                  {t(
                    'atlas.catalog_exports.date_range_help_text',
                    `Use this field to select a date range starting from the previous day. The date ranges begin at 00:00:00 UTC on the start date and end at 23:59:59 UTC on the end date.`,
                    'Text that appears on tooltip',
                  )}
                </Tooltip>
              )
            }
            onShow={() => {
              sendEvent({
                eventCategory: EVENT_CATEGORY.CATALOG_EXPORTS_FORM,
                eventAction: EVENT_ACTION.HOVER,
                eventLabel: 'Date range tooltip opened',
                product: GA_PRODUCT_CATALOG,
              });
              setShowTooltip(true);
            }}
            onHide={() => setShowTooltip(false)}
            placement="topEnd"
          >
            <StyledIconHelpCircle
              aria-label="Date range type"
              iconSize={16}
              semanticColor="textSubdued"
            />
          </TooltipTrigger>
        </DateRangeLabelContainer>
      }
      labelFor={triggerId}
    >
      {showDateRangePicker && (
        <DateRangePickerWrapper
          onCloseModal={() => setShowDateRangePicker(false)}
          dispatch={dispatch}
        />
      )}
      <DropdownTrigger
        name="date-range"
        overlay={
          showDropdown && (
            <DropdownList
              onKeyDown={e => onKeyDown(e)}
              onBlur={() => setShowDropdown(false)}
              id={dropdownListId}
              name="date-range"
              data-testid={dropdownListId}
              aria-activedescendant={selectedDateRangeTypeId}
            >
              {items.map((item, index) => (
                <DropdownLink
                  type="li"
                  as="li"
                  role="option"
                  id={item.id}
                  name={item.id}
                  key={item.id}
                  onFocus={() => setSelected(index)}
                  onClick={e => {
                    e.preventDefault();
                    handleClick(item.id);
                  }}
                  selected={selectedDateRangeTypeId === item.id}
                  aria-selected={selectedDateRangeTypeId === item.id}
                >
                  {item.id === DATE_RANGE_TYPE.CUSTOM_DATES &&
                  (errors.startDate || errors.endDate)
                    ? t(
                        'atlas.date_range_types.custom_date_range',
                        'Custom date range',
                        'Refers to a custom date range',
                      )
                    : items[index].text}
                </DropdownLink>
              ))}
            </DropdownList>
          )
        }
        onShow={e => {
          e.preventDefault();
          setShowDropdown(true);
        }}
        onHide={() => {
          setShowDropdown(false);
          const element = document.getElementById(triggerId) as HTMLElement;
          element.focus();
        }}
      >
        <StyledDropdown
          name="date-range"
          id={triggerId}
          type="button"
          aria-labelledby={`dropdown-label ${triggerId}`}
          aria-expanded={showDropdown || undefined}
        >
          {renderDropdownText()}
        </StyledDropdown>
      </DropdownTrigger>
      {(hasStartDateError || hasEndDateError) && (
        <FormHelpText error>
          {t(
            'atlas.catalog_exports.date_range.validation.valid_start_and_end_dates',
            'Must have valid start and end dates',
            'Validation text that appears under date range type input',
          )}
        </FormHelpText>
      )}
      {!!onboardingPopover && onboardingPopover}
    </StyledFormGroup>
  );
};
